import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { BadgePercent, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { useCartStore } from "@/stores/cartStore";
import { fetchProductByHandle } from "@/lib/shopify";
import { computeBundlePricing } from "@/lib/bundles";
import { TIERED_PACK_PRICING } from "@/routes/products.$handle";
import { toast } from "sonner";
import { SOLD_OUT, isComingSoon } from "@/lib/storeStatus";

interface FbtRule {
  /** Recommended product handle. */
  handle: string;
  /** Short subtitle under the recommended product name. */
  subtitle: string;
  /** Short subtitle under the current (left) product name. */
  leftSubtitle: string;
  /** Plain-language note on who the pairing actually suits. */
  suitedFor: string;
}

/**
 * Configurable Frequently-Bought-Together pairings. Add more entries to
 * enable the section on additional PDPs.
 *
 * This is an OPTIONAL add-on shown after the primary purchase action, and the
 * add-on always starts unselected — the visitor finishes one decision first.
 */
const FBT_RULES: Record<string, FbtRule> = {
  // Liver + gut reset pairs naturally with hormone/cycle balance (shared
  // oestrogen-clearance and metabolic pathways).
  "liver-biome-plus": {
    handle: "ova-balance",
    subtitle: "Hormone + Cycle Balance",
    leftSubtitle: "Liver + Gut",
    suitedFor:
      "Suits women also managing PCOS, irregular cycles or hormonal acne. If that's not you, skip it — no shared actives are lost.",
  },
  "mag-absorb-pro": {
    handle: "liver-biome-plus",
    subtitle: "Liver + Gut Reset",
    leftSubtitle: "Deep Sleep + Recovery",
    suitedFor:
      "Suits people whose poor sleep comes with bloating, sluggish digestion or fatty-liver concerns. No overlapping actives between the two.",
  },
  "ova-balance": {
    handle: "liver-biome-plus",
    subtitle: "Liver + Gut Reset",
    leftSubtitle: "Hormone + Cycle Balance",
    suitedFor:
      "Suits PCOS routines where gut and liver support matters too, since oestrogen clearance runs through the liver. No overlapping actives.",
  },
};

const RECOMMENDED_TITLE: Record<string, string> = {
  "mag-absorb-pro": "Mag Absorb Pro",
  "liver-biome-plus": "Liver Biome Plus",
  "ova-balance": "Ova Balance",
};

function inr(n: number) {
  return `₹${Math.round(n).toLocaleString("en-IN")}`;
}

function pushFbtEvent(payload: Record<string, unknown>) {
  if (typeof window === "undefined") return;
  const w = window as unknown as { dataLayer?: unknown[] };
  w.dataLayer = w.dataLayer || [];
  w.dataLayer.push(payload);
}

/** True when this handle has a configured add-on pairing worth showing. */
export function hasFbtOffer(handle: string): boolean {
  if (SOLD_OUT) return false;
  const r = FBT_RULES[handle];
  if (!r) return false;
  if (isComingSoon(r.handle)) return false;
  return !!TIERED_PACK_PRICING[handle];
}

export interface FrequentlyBoughtTogetherProps {
  /** Current product handle. */
  handle: string;
  /** Currently selected pack size from PackSizeSelector (1 | 2 | 3). */
  qty: number;
  /** Current product image (left tile). */
  currentImage: string | null;
  /** Current product title (left tile). */
  currentTitle: string;
  /** Variant id of the currently selected pack (for cart line). */
  currentVariantId: string;
  /** Hide the "Optional add-on" divider heading (used inside the pre-book sheet). */
  hideHeading?: boolean;
  /** Called after the lines are successfully added to the cart. */
  onAdded?: () => void;
}

export function FrequentlyBoughtTogether({
  handle,
  qty,
  currentImage,
  currentTitle,
  currentVariantId,
  hideHeading = false,
  onAdded,
}: FrequentlyBoughtTogetherProps) {
  const rule = FBT_RULES[handle];
  const addItems = useCartStore((s) => s.addItems);
  const isLoading = useCartStore((s) => s.isLoading);
  // Add-ons always start UNSELECTED — never pre-decide a second purchase.
  const [checked, setChecked] = useState(false);

  // Load recommended product for variantId + live image, only when a rule exists.
  const recQuery = useQuery({
    queryKey: ["fbt-product", rule?.handle],
    queryFn: () => fetchProductByHandle(rule!.handle),
    enabled: !!rule,
    staleTime: 5 * 60 * 1000,
  });

  const leftPack = TIERED_PACK_PRICING[handle]?.[qty as 1 | 2 | 3];
  const rightPack = rule ? TIERED_PACK_PRICING[rule.handle]?.[1] : null;
  // Cart lines must carry the 1-pack UNIT price with quantity = pack size.
  // The cart's tiered engine (bundleDiscount.ts) then applies pack savings
  // (unit × qty − pack price). Passing the pack price here with quantity > 1
  // would double-charge and fail the engine's unit-price guard.
  const leftUnitPrice = TIERED_PACK_PRICING[handle]?.[1]?.price;

  const { bundlePrice, listPrice, savings, bundleRate } = useMemo(() => {
    const lp = leftPack?.price ?? 0;
    const rp = rightPack?.price ?? 0;
    if (!checked) {
      return { bundlePrice: lp, listPrice: lp, savings: 0, bundleRate: 0 };
    }
    const { subtotal, total, savings: save, rate } = computeBundlePricing([lp, rp]);
    return { bundlePrice: total, listPrice: subtotal, savings: save, bundleRate: rate };
  }, [leftPack, rightPack, checked]);


  if (!rule || !leftPack) return null;

  const recProduct = recQuery.data as any;
  const recNode = recProduct?.node ?? recProduct;
  const recVariant = recNode?.variants?.edges?.[0]?.node;
  const recImage = recNode?.images?.edges?.[0]?.node?.url ?? null;
  const recTitle = RECOMMENDED_TITLE[rule.handle] ?? recNode?.title ?? "";
  const canAddRecommended =
    !!recVariant &&
    !!rightPack &&
    recVariant.availableForSale !== false &&
    !isComingSoon(rule.handle);

  // Suppress bundles that can't actually be bought right now — no dead add-ons.
  if (SOLD_OUT) return null;
  if (isComingSoon(rule.handle)) return null;
  if (recQuery.isSuccess && !canAddRecommended) return null;

  async function handleAdd() {
    if (!currentVariantId) {
      toast.error("Please pick a pack first.");
      return;
    }
    const lines: Parameters<typeof addItems>[0] = [
      {
        variantId: currentVariantId,
        productHandle: handle,
        productTitle: currentTitle,
        imageUrl: currentImage,
        price: {
          amount: String(leftUnitPrice ?? Math.round(leftPack!.price / qty)),
          currencyCode: "INR",
        },
        quantity: qty,
      },
    ];
    if (checked && recVariant && rightPack) {
      lines.push({
        variantId: recVariant.id,
        productHandle: rule.handle,
        productTitle: recTitle,
        imageUrl: recImage,
        price: { amount: recVariant.price.amount, currencyCode: recVariant.price.currencyCode },
        quantity: 1,
      });
    }
    const res = await addItems(lines);
    if (!res) {
      toast.error("Couldn't add to cart. Please try again.");
      return;
    }
    pushFbtEvent({
      event: "frequently_bought_together_add",
      product_id: handle,
      selected_pack: qty,
      recommended_product_id: rule.handle,
      bundle_price: bundlePrice,
      savings,
      checkbox_state: checked ? "checked" : "unchecked",
    });
    toast.success(checked ? "Bundle added to cart" : "Added to cart");
    onAdded?.();
  }

  const ctaLabel = checked ? "Add bundle to cart" : `Add ${currentTitle} to cart`;

  return (
    <section
      className={hideHeading ? "" : "mt-8"}
      aria-labelledby={hideHeading ? undefined : "fbt-heading"}
      aria-label={hideHeading ? "Frequently bought together" : undefined}
    >
      <div className="overflow-hidden rounded-2xl border border-brand/35 bg-card shadow-sm">
        <div className="bg-brand-soft px-4 pb-4 pt-5 sm:px-5">
          {!hideHeading && (
            <h3 id="fbt-heading" className="text-xl font-semibold text-foreground sm:text-2xl">
              Frequently bought together
            </h3>
          )}
          <div className={cn("flex items-center justify-center gap-2 rounded-xl bg-background/80 px-3 py-3 text-center text-sm font-medium text-brand", !hideHeading && "mt-4")}>
            <BadgePercent className="h-5 w-5 shrink-0" aria-hidden="true" />
            Bundle up and save {Math.round(bundleRate * 100) || 5}%
          </div>
        </div>

        <div className="space-y-3 p-4 sm:p-5">
          <Tile
            image={currentImage}
            title={currentTitle}
            subtitle={`${rule.leftSubtitle} · Pack of ${qty}`}
            price={leftPack.price}
            mrp={leftPack.mrp}
            selected
          />
          <Tile
            image={recImage}
            title={recTitle || "…"}
            subtitle={rule.subtitle}
            price={rightPack?.price}
            mrp={rightPack?.mrp}
            selectable
            selected={checked}
            onToggle={() => canAddRecommended && setChecked((v) => !v)}
            disabled={!canAddRecommended}
          />

          <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 pt-1">
            <span className="text-base font-medium text-foreground">Bundle price</span>
            <div className="flex flex-wrap items-baseline justify-end gap-2">
              {savings > 0 && <span className="text-sm text-muted-foreground line-through">{inr(listPrice)}</span>}
              <strong className="text-xl font-semibold text-foreground">{inr(bundlePrice)}</strong>
            </div>
          </div>
          {savings > 0 && (
            <p className="text-right text-xs font-medium text-brand">
              Save {inr(savings)} · discount applied in cart
            </p>
          )}

          <Button
            type="button"
            size="lg"
            onClick={handleAdd}
            disabled={isLoading}
            data-ci-click="pdp-bundle-add-to-cart"
            className="h-12 w-full rounded-xl text-sm font-semibold"
          >
            {isLoading ? "Adding…" : ctaLabel}
          </Button>
        </div>
      </div>

      <p className="mt-2.5 text-xs leading-relaxed text-muted-foreground">{rule.suitedFor}</p>
    </section>
  );
}

interface TileProps {
  image: string | null;
  title: string;
  subtitle: string;
  price?: number;
  mrp?: number;
  selectable?: boolean;
  selected?: boolean;
  disabled?: boolean;
  onToggle?: () => void;
}

function Tile({
  image,
  title,
  subtitle,
  price,
  mrp,
  selectable,
  selected,
  disabled,
  onToggle,
}: TileProps) {
  const content = (
      <div className="flex w-full items-center gap-3">
        <div className="relative h-20 w-20 flex-shrink-0 overflow-hidden rounded-xl bg-muted sm:h-24 sm:w-24">
          {image ? (
            <img
              src={image}
              alt={title}
              width={80}
              height={80}
              loading="lazy"
              className="h-full w-full object-contain"
            />
          ) : (
            <div className="h-full w-full bg-muted" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold leading-snug text-foreground sm:text-base">
            {title}
          </div>
          <div className="mt-1 text-xs leading-snug text-muted-foreground">
            {subtitle}
          </div>
          {typeof price === "number" && (
            <div className="mt-2 flex flex-wrap items-baseline gap-2">
              {typeof mrp === "number" && mrp > price && <span className="text-xs text-muted-foreground line-through">{inr(mrp)}</span>}
              <strong className="text-sm font-semibold text-brand sm:text-base">{inr(price)}</strong>
            </div>
          )}
        </div>
        <span
          className={cn(
            "flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border",
            selected ? "border-primary bg-primary text-primary-foreground" : "border-input bg-background text-transparent",
          )}
          aria-hidden="true"
        >
          <Check className="h-4 w-4" strokeWidth={3} />
        </span>
      </div>
  );

  if (selectable) {
    return (
      <button
        type="button"
        onClick={onToggle}
        disabled={disabled}
        aria-pressed={!!selected}
        data-ci-click="pdp-bundle-item-toggle"
        className={cn(
          "w-full rounded-xl border bg-card p-3 text-left transition-colors",
          selected
            ? "border-brand/50"
            : "border-border hover:border-brand/30",
          disabled && "opacity-60",
        )}
      >
        {content}
      </button>
    );
  }
  return (
    <div className="w-full rounded-xl border border-border bg-card p-3">
      {content}
    </div>
  );
}
